import { ApolloError, useMutation, useQuery } from "@apollo/client";
import { useStyletron } from "baseui";
import { KIND } from "baseui/button";
import { ConfirmDialog } from "components/confirm-dialog";
import { Content } from "components/content";
import { Header } from "components/header";
import { NoPermissionsRedirect } from "components/no-permissions-redirect";
import { ObjectViewRenderer } from "components/object-view-renderer";
import { useAuth } from "contexts/auth-context";
import { useLoading } from "contexts/loading-context";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { Pencil, Trash } from "tabler-icons-react";
import { PERMISSIONS } from "utils/permissions";

import { CustomSetting } from "../custom-settings";
import { CUSTOM_SETTINGS_FIELDS } from "../custom-settings.form";
import {
  CUSTOM_SETTINGS_DELETE,
  CUSTOM_SETTINGS_SHOW,
} from "../custom-settings.gql";

export default function CustomSettingsShow(): React.ReactElement {
  const [, theme] = useStyletron();
  const { enqueueSnackbar } = useSnackbar();
  const { checkPermission } = useAuth();
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const history = useHistory();
  const { id } = useParams<{ id?: string }>();
  const { setIsFetching, setIsLoading } = useLoading();

  const { refetch, data, error } = useQuery(CUSTOM_SETTINGS_SHOW, {
    variables: { id: id ? parseInt(id) : null },
    notifyOnNetworkStatusChange: true,
  });
  const customSetting: CustomSetting = data?.customSetting;

  useEffect(() => {
    if (error?.graphQLErrors)
      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
  }, [error]);

  useEffect(() => {
    if (data?.customSetting) refetch();
    setIsFetching(true);
  }, []);

  useEffect(() => data?.customSetting && setIsFetching(false), [data]);

  const [deleteCustomSetting] = useMutation(CUSTOM_SETTINGS_DELETE);

  const submit = async () => {
    setIsLoading(true);

    try {
      await deleteCustomSetting({
        variables: {
          customSettingDeleteInput: { id: id ? parseInt(id) : null },
        },
      });

      enqueueSnackbar({
        message: "Usunięto pomyślnie",
        variant: "success",
      });

      setIsConfirmDialogOpen(false);

      history.push("/custom-settings");
    } catch (error: unknown) {
      setIsConfirmDialogOpen(false);

      enqueueSnackbar({
        message: (error as ApolloError).graphQLErrors.map(
          ({ message }) => message
        )[0],
        variant: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!checkPermission(PERMISSIONS.customSetting.read))
    return <NoPermissionsRedirect />;

  return (
    <article>
      <Header
        title={customSetting?.name}
        labels={["Inne treści", "Wyświetlanie"]}
        goBackOption
        buttons={[
          {
            label: "Edytuj",
            kind: KIND.secondary,
            startEnhancer: <Pencil size={18} />,
            permission: checkPermission(PERMISSIONS.customSetting.update),
            onClick: () => history.push(`/custom-settings/${id}/update`),
          },
        ]}
        actions={[
          {
            label: "Usuń pozycję",
            icon: Trash,
            color: theme.colors.negative,
            onClick: () => setIsConfirmDialogOpen(true),
            permission: checkPermission(PERMISSIONS.customSetting.delete),
          },
        ]}
      />
      <Content>
        {data && (
          <ObjectViewRenderer
            fields={CUSTOM_SETTINGS_FIELDS}
            data={customSetting}
          />
        )}
        <ConfirmDialog
          isOpen={isConfirmDialogOpen}
          label={`Usunięcie pozycji ${customSetting?.name}`}
          close={() => setIsConfirmDialogOpen(false)}
          confirm={submit}
        />
      </Content>
    </article>
  );
}
